import React, { useEffect, useState } from 'react';
import { Box, Button, Chip, Container, Typography, Link, Stack, Divider, Alert, Tooltip } from '@mui/material';
import { AccountBalanceWallet } from '@mui/icons-material';
import LoadingContent, { ErrorContent } from '../../components/LoadingContent';
import server from '../../global/server';
import { useSnackbar } from 'notistack';
import { QRCodeCanvas } from 'qrcode.react';
import { useApp } from '../../context/AppContext';

const statusLabels = {
    pending: 'Pendente',
    analyzing: 'Em Análise',
    approved: 'Aprovado',
    refused: 'Recusado',
    suspended: 'Suspenso',
    affiliation: 'Afiliação',
    active: 'Ativo',
};

export default function ReceiverStatus() {
    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [qrCodeData, setQrCodeData] = useState(null);
    const { enqueueSnackbar } = useSnackbar();
    const { socket } = useApp();

    const fetchStatus = async () => {
        setIsLoading(true);
        try {
            const response = await server.get('/receiver/status');
            const receiverData = response.data.data;

            if (!receiverData) {
                throw new Error('Dados do recebedor não encontrados');
            }

            setData(receiverData);

            if (receiverData.kycQRCodeData) {
                setQrCodeData(receiverData.kycQRCodeData);
            }

            setError(null);
        } catch (err) {
            console.error('Erro ao buscar o status:', err);
            setError('Erro ao carregar status. Tente novamente.');
        } finally {
            setIsLoading(false);
        }
    };

    const isQRCodeExpired = (expiresAt) => {
        return new Date(expiresAt) < new Date();
    };

    useEffect(() => {
        fetchStatus();

        if (socket) {
            socket.on(`kyc_update_${data?.recipient_id}`, (update) => {
                if (update) {
                    setData((prevData) => ({
                        ...prevData,
                        kyc_status: update.kyc_status || prevData.kyc_status,
                    }));

                    if (update.kycQRCodeData) {
                        setQrCodeData(update.kycQRCodeData);
                    }
                }
            });
        }

        return () => {
            if (socket) {
                socket.off(`kyc_update_${data?.recipient_id}`);
            }
        };
    }, [socket, data?.recipient_id]);

    const handleProofOfLifeRequest = async () => {
        try {
            const response = await server.get('/receiver/kyc-qrcode');
            enqueueSnackbar('Prova de vida solicitada com sucesso!', { variant: 'success' });

            if (response.data.data) {
                setQrCodeData(response.data.data);
            }
        } catch (error) {
            enqueueSnackbar(error.response?.data?.message || 'Erro ao solicitar prova de vida.', {
                variant: 'error',
            });
        }
    };

    if (isLoading) return <LoadingContent />;
    if (error) return <ErrorContent message={error} />;

    return (
        <Container maxWidth="sm" sx={{ pt: 4, position: 'relative' }}>
            {/* Saldo do Recebedor no canto superior direito */}
            {data?.balanceDetails && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 16,
                        right: 16,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        backgroundColor: 'background.paper',
                        borderRadius: 2,
                        p: 1,
                        boxShadow: 1,
                    }}
                >
                    <Tooltip title="Saldo disponível">
                        <AccountBalanceWallet color="primary" />
                    </Tooltip>
                    <Typography variant="body1" color="primary">
                        R$ {data.balanceDetails.available.toFixed(2)}
                    </Typography>
                </Box>
            )}

            {/* Conteúdo principal */}
            <Box
                sx={{
                    p: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    textAlign: 'center',
                    boxShadow: 2,
                    backgroundColor: 'background.default',
                }}
            >
                <Typography variant="h4" mb={3} fontWeight="bold">
                    Status do Recebedor
                </Typography>

                <Stack spacing={3} divider={<Divider flexItem />} sx={{ mb: 3 }}>
                    <Box>
                        <Typography variant="h6" gutterBottom>
                            Status Atual
                        </Typography>
                        <Chip
                            label={statusLabels[data?.receiver_status] || 'Desconhecido'}
                            color="primary"
                            size="medium"
                            sx={{ fontSize: '1rem', py: 1 }}
                        />
                    </Box>

                    {data?.receiver_status === 'active' && (
                        <Box>
                            <Typography variant="h6" gutterBottom>
                                Prova de Vida
                            </Typography>

                            {qrCodeData ? (
                                !isQRCodeExpired(qrCodeData.expires_at) ? (
                                    <>
                                        <QRCodeCanvas value={qrCodeData.url} size={150} />
                                        <Alert severity="info" sx={{ mt: 2 }}>
                                            QR Code válido até{' '}
                                            {new Date(qrCodeData.expires_at).toLocaleString()}
                                        </Alert>
                                        <Typography
                                            variant="body2"
                                            sx={{ mt: 2, wordWrap: 'break-word' }}
                                        >
                                            <Link
                                                href={qrCodeData.url}
                                                target="_blank"
                                                rel="noopener"
                                            >
                                                {qrCodeData.url}
                                            </Link>
                                        </Typography>
                                    </>
                                ) : (
                                    <>
                                        <Alert severity="error" sx={{ mt: 2 }}>
                                            QR Code expirado. Solicite um novo.
                                        </Alert>
                                        <Button
                                            onClick={handleProofOfLifeRequest}
                                            variant="contained"
                                            sx={{ mt: 2 }}
                                        >
                                            Solicitar Prova de Vida
                                        </Button>
                                    </>
                                )
                            ) : (
                                <Button
                                    onClick={handleProofOfLifeRequest}
                                    variant="contained"
                                    sx={{ mt: 2 }}
                                >
                                    Solicitar Prova de Vida
                                </Button>
                            )}
                        </Box>
                    )}
                </Stack>

                {data?.receiver_status !== 'active' && (
                    <Alert severity="warning">
                        Recebedor ainda não está ativo. Aguarde a análise dos dados. Caso o status não mude em breve, entre em contato com o suporte.
                    </Alert>
                )}
            </Box>
        </Container>
    );
}
